/**
 * Loading placeholders, shaped like the content they stand in for.
 *
 * A spinner says "wait" and nothing else; a skeleton says "a list of four things is coming, roughly
 * this size", so the layout does not jump when the data lands. Several panels here previously showed
 * a bare spinner, a lonely line of text, or nothing at all — three different answers to the same
 * question. This is the one answer.
 *
 * `aria-hidden` throughout: the shapes are decorative, and the region that owns them announces the
 * loading state once via aria-busy rather than having a screen reader read out empty boxes.
 */

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`skeleton rounded-field ${className}`} aria-hidden="true" />;
}

/** A stack of text lines; the last is short so it reads as a paragraph rather than a block. */
export function SkeletonText({ lines = 3, className = '' }: { lines?: number; className?: string }) {
  return (
    <div className={`space-y-2 ${className}`} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={`h-3 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`} />
      ))}
    </div>
  );
}

/** Avatar + two lines — people lists, collaborators, anything person-shaped. */
export function SkeletonRow({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center gap-3 ${className}`} aria-hidden="true">
      <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-2.5 w-1/3" />
      </div>
    </div>
  );
}

/** Media/sprite tiles — a grid of squares in the same rhythm as the real thing. */
export function SkeletonTiles({ count = 6, className = '' }: { count?: number; className?: string }) {
  return (
    <div className={`grid grid-cols-2 gap-2 sm:grid-cols-3 ${className}`} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} className="aspect-square w-full" />
      ))}
    </div>
  );
}
